import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import {
    marketingBlogEntity,
    marketingMarketingRecommendationEntity,
    marketingNewsletterEntity,
    marketingSocialPostEntity,
    marketingObjectiveEntity,
    marketingAgentLearningEntity,
} from 'mj_generatedentities';

/**
 * Home — "Good morning, here's what needs you today" mockup pattern.
 *
 * Sections (all wired to live data where it exists; skeletons where it doesn't):
 *   1. Page hero (eyebrow + serif-feel headline + ask-the-agent CTA)
 *   2. Objectives band — 3 OKR cards (skeletons until objectives entity ships in Phase 2)
 *   3. Attention card — open recommendations from the Strategy Agent (real)
 *   4. Shipping this week — cross-channel queue (real, derived from blogs/social/newsletters)
 *   5. Pipeline + Recent learnings — KPIs from analytics (skeletons until provider data lands)
 *   6. Quick links — 4 nav tiles (real)
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-home',
    template: `
<!-- Hero -->
<div class="hm__hero">
    <div class="hm__hero-left">
        <div class="hm__eyebrow">Home · {{ todayLabel }}{{ companyName ? ' · ' + companyName : '' }}</div>
        <h1 class="hm__title">{{ greeting }}. Here's what needs you today.</h1>
    </div>
</div>

<!-- Objectives band -->
<section class="hm__section" *ngIf="objectives.length > 0">
    <div class="hm__caps">Quarterly objectives</div>
    <div class="hm__okr-grid">
        <button *ngFor="let o of objectives.slice(0, 3); let i = index" type="button" class="hm__okr-card" (click)="navigate('strategy/objectives')">
            <div class="hm__okr-head">
                <span class="hm__caps">Obj {{ pad(i + 1) }}</span>
                <mkt-ws-pill [variant]="objectiveVariant(o.Status)" [dot]="true">{{ objectiveLabel(o.Status) }}</mkt-ws-pill>
            </div>
            <div class="hm__okr-title">{{ o.Title }}</div>
            <div class="hm__okr-progress" *ngIf="o.TargetValue != null">
                <span class="hm__caps">{{ o.TargetUnit || 'Progress' }}</span>
                <span class="hm__okr-num">{{ formatNum(o.CurrentValue) }} / {{ formatNum(o.TargetValue) }}</span>
            </div>
            <div class="hm__okr-bar" *ngIf="o.TargetValue != null">
                <div class="hm__okr-bar-fill" [style.width.%]="progressPct(o)"></div>
            </div>
        </button>
    </div>
</section>
<!-- Skeleton fallback if no objectives are loaded yet -->
<section class="hm__section" *ngIf="objectives.length === 0 && !objectivesLoaded">
    <div class="hm__caps">Quarterly objectives</div>
    <div class="hm__okr-grid">
        <mkt-ws-card *ngFor="let i of [0,1,2]">
            <div class="hm__okr-head">
                <mkt-ws-skel variant="badge" width="80px"></mkt-ws-skel>
                <mkt-ws-skel variant="pill" width="60px"></mkt-ws-skel>
            </div>
            <mkt-ws-skel variant="text-lg" width="80%" style="margin: 8px 0 12px;"></mkt-ws-skel>
            <div class="hm__okr-progress">
                <mkt-ws-skel variant="text-md" width="40%"></mkt-ws-skel>
                <mkt-ws-skel variant="badge" width="30px"></mkt-ws-skel>
            </div>
            <mkt-ws-skel variant="bar-track" width="100%"></mkt-ws-skel>
        </mkt-ws-card>
    </div>
</section>

<!-- Attention card — open recommendations -->
<section class="hm__section">
    <mkt-ws-card [hasHead]="true" [hasFoot]="true" [noPadding]="true">
        <div head style="display:flex; align-items:center; gap:8px;">
            <span class="hm__caps-ink">Your attention today</span>
            <mkt-ws-pill *ngIf="recommendations.length > 0" variant="warn">{{ recommendations.length }}</mkt-ws-pill>
            <span style="margin-left:auto; font-size: 11px; color: var(--mj-text-secondary, #71717a);">Open recommendations from the Strategy Agent</span>
        </div>

        <ng-container *ngIf="recommendations.length > 0; else attentionEmpty">
            <button *ngFor="let r of recommendations.slice(0, 5)" class="hm__att-row" type="button" (click)="navigate('strategy/recommendations')">
                <div class="hm__att-pills">
                    <mkt-ws-pill [variant]="categoryVariant(r.Category)">{{ r.Category }}</mkt-ws-pill>
                </div>
                <div class="hm__att-body">
                    <div class="hm__att-title">{{ r.Title }}</div>
                    <div class="hm__att-detail" *ngIf="r.Detail">{{ r.Detail }}</div>
                </div>
            </button>
        </ng-container>
        <ng-template #attentionEmpty>
            <div *ngFor="let i of [0,1,2]" class="hm__att-row hm__att-row--skel">
                <mkt-ws-skel variant="badge" width="60px"></mkt-ws-skel>
                <div class="hm__att-body">
                    <mkt-ws-skel variant="text-md" width="70%"></mkt-ws-skel>
                    <mkt-ws-skel variant="text-sm" width="55%" style="margin-top: 6px;"></mkt-ws-skel>
                </div>
            </div>
        </ng-template>

        <div foot>
            <span>Strategy Agent surfaces these — Apply or Dismiss in the Recommendations inbox.</span>
            <button type="button" class="hm__foot-link" (click)="navigate('strategy/recommendations')">Recommendations <i class="fa-solid fa-arrow-right"></i></button>
        </div>
    </mkt-ws-card>
</section>

<!-- Two-column: Shipping + Pipeline/Learnings -->
<section class="hm__section hm__split">
    <!-- Shipping this week (2/3) -->
    <mkt-ws-card class="hm__split-main" [hasHead]="true" [hasFoot]="true" [noPadding]="true">
        <div head style="display:flex; align-items:center; gap:8px;">
            <span class="hm__caps-ink">Shipping this week</span>
            <span style="margin-left:auto; font-size: 11px; color: var(--mj-text-secondary, #71717a);">{{ shippingItems.length }} items across channels</span>
        </div>
        <table class="hm__queue">
            <thead>
                <tr>
                    <th>Channel</th>
                    <th>Asset</th>
                    <th class="hm__queue-th-r">Status</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let item of shippingItems.slice(0, 8)">
                    <td><mkt-ws-pill variant="muted">{{ item.channel }}</mkt-ws-pill></td>
                    <td class="hm__queue-asset">{{ item.title }}</td>
                    <td class="hm__queue-th-r"><mkt-ws-pill [variant]="item.statusVariant">{{ item.status }}</mkt-ws-pill></td>
                </tr>
                <tr *ngIf="shippingItems.length === 0">
                    <td colspan="3" style="padding: 24px; text-align: center; color: var(--mj-text-secondary, #71717a); font-size: 12px;">
                        Nothing in flight this week. Trigger a scheduled job from Foundation → Activity log to generate content.
                    </td>
                </tr>
            </tbody>
        </table>
        <div foot>
            <span>Drafts, scheduled, and recent publishes.</span>
            <button type="button" class="hm__foot-link" (click)="navigate('programs')">Open Programs <i class="fa-solid fa-arrow-right"></i></button>
        </div>
    </mkt-ws-card>

    <!-- Pipeline + learnings (1/3) -->
    <div class="hm__split-side">
        <mkt-ws-card>
            <div class="hm__pipeline-head">
                <span class="hm__caps">Pipeline · 30d</span>
                <i class="fa-solid fa-arrow-trend-up" style="color: var(--mj-text-secondary, #71717a);"></i>
            </div>
            <mkt-ws-skel variant="text-xl" width="50%" style="margin: 4px 0;"></mkt-ws-skel>
            <mkt-ws-skel variant="text-sm" width="80%"></mkt-ws-skel>
            <div class="hm__pipeline-grid">
                <div>
                    <div class="hm__pipeline-label">MRR closed</div>
                    <mkt-ws-skel variant="text-md" width="80%"></mkt-ws-skel>
                </div>
                <div>
                    <div class="hm__pipeline-label">Attributed ARR</div>
                    <mkt-ws-skel variant="text-md" width="80%"></mkt-ws-skel>
                </div>
            </div>
        </mkt-ws-card>

        <mkt-ws-card [hasHead]="true" [noPadding]="true">
            <div head style="display: flex; align-items: center; justify-content: space-between;">
                <span class="hm__caps-ink">Recent learnings</span>
                <button type="button" class="hm__foot-link" style="font-size: 11px;" (click)="navigate('experiments/learnings')">All →</button>
            </div>
            <ng-container *ngIf="recentLearnings.length > 0; else learningSkel">
                <button *ngFor="let l of recentLearnings.slice(0, 2)" type="button" class="hm__learning hm__learning--btn" (click)="openLearning(l.ID)">
                    <mkt-ws-pill [variant]="learningVariant(l.Confidence)">{{ l.Confidence || '—' }}</mkt-ws-pill>
                    <div class="hm__learning-title">{{ l.Finding }}</div>
                </button>
            </ng-container>
            <ng-template #learningSkel>
                <div class="hm__learning" *ngFor="let i of [0,1]">
                    <mkt-ws-skel variant="badge" width="80px"></mkt-ws-skel>
                    <mkt-ws-skel variant="text-md" width="100%" style="margin-top: 6px;"></mkt-ws-skel>
                </div>
            </ng-template>
        </mkt-ws-card>
    </div>
</section>

<!-- Quick links -->
<section class="hm__section">
    <div class="hm__caps">Jump in</div>
    <div class="hm__quick">
        <button *ngFor="let q of quickLinks" type="button" class="hm__quick-tile" (click)="navigate(q.path)">
            <div class="hm__quick-head">
                <span class="hm__caps">{{ q.label }}</span>
                <i class="fa-solid fa-arrow-up-right-from-square" style="color: var(--mj-text-disabled, #aaa); font-size: 11px;"></i>
            </div>
            <div class="hm__quick-title">{{ q.title }}</div>
            <div class="hm__quick-sub">{{ q.sub }}</div>
        </button>
    </div>
</section>
    `,
    styles: [`
        :host { display: block; }
        .hm__hero { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 32px; gap: 24px; }
        .hm__hero-left { flex: 1; min-width: 0; }
        .hm__eyebrow, .hm__caps { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-bottom: 12px; }
        .hm__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .hm__title { font-size: 36px; line-height: 1.05; letter-spacing: -0.02em; font-weight: 600; margin: 0; max-width: 760px; color: var(--mj-text-primary, #1a1a1a); }

        .hm__section + .hm__section { margin-top: 32px; }

        .hm__okr-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .hm__okr-head { display: flex; align-items: center; justify-content: space-between; }
        .hm__okr-progress { display: flex; align-items: end; justify-content: space-between; margin-bottom: 8px; }

        .hm__att-row { display: flex; align-items: flex-start; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); border-left: none; border-right: none; border-top: none; text-decoration: none; color: inherit; cursor: pointer; transition: background 0.15s; width: 100%; text-align: left; background: transparent; font-family: inherit; font-size: inherit; }
        .hm__att-row:last-child { border-bottom: none; }
        .hm__att-row:hover { background: var(--mj-bg-page, #f5f5f5); }
        .hm__att-row--skel { cursor: default; }
        .hm__att-row--skel:hover { background: transparent; }
        .hm__att-pills { display: flex; gap: 4px; flex-shrink: 0; padding-top: 2px; }
        .hm__att-body { flex: 1; min-width: 0; }
        .hm__att-title { font-size: 13.5px; font-weight: 500; line-height: 1.4; color: var(--mj-text-primary, #1a1a1a); margin-bottom: 4px; }
        .hm__att-detail { font-size: 12px; color: var(--mj-text-secondary, #71717a); line-height: 1.5; }

        .hm__split { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; }
        .hm__split-main, .hm__split-side { min-width: 0; }
        .hm__split-side { display: flex; flex-direction: column; gap: 16px; }

        .hm__queue { width: 100%; border-collapse: collapse; font-size: 13px; }
        .hm__queue thead { background: var(--mj-bg-page, #f5f5f5); }
        .hm__queue th { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); padding: 10px 16px; text-align: left; }
        .hm__queue-th-r { text-align: right !important; }
        .hm__queue tbody tr { border-top: 1px solid var(--mj-border-default, #e0e0e0); transition: background 0.15s; }
        .hm__queue tbody tr:hover { background: var(--mj-bg-page, #f5f5f5); }
        .hm__queue td { padding: 10px 16px; vertical-align: middle; }
        .hm__queue-asset { font-weight: 500; color: var(--mj-text-primary, #1a1a1a); max-width: 480px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hm__queue td:last-child { text-align: right; }

        .hm__pipeline-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .hm__pipeline-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
        .hm__pipeline-label { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-bottom: 4px; }

        .hm__learning { padding: 14px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .hm__learning:last-child { border-bottom: none; }
        .hm__learning--btn { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; width: 100%; text-align: left; background: transparent; border: none; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); cursor: pointer; font: inherit; transition: background 0.15s; }
        .hm__learning--btn:last-child { border-bottom: none; }
        .hm__learning--btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .hm__learning-title { font-size: 13px; color: var(--mj-text-primary, #1a1a1a); line-height: 1.45; }

        .hm__okr-card { display: block; padding: 18px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; cursor: pointer; font: inherit; text-align: left; transition: border-color 0.15s; }
        .hm__okr-card:hover { border-color: var(--mj-brand-primary, #6366f1); }
        .hm__okr-title { font-size: 14px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin: 8px 0 12px; line-height: 1.35; }
        .hm__okr-num { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); }
        .hm__okr-bar { height: 6px; background: var(--mj-bg-page, #f5f5f5); border-radius: 3px; overflow: hidden; margin-top: 6px; }
        .hm__okr-bar-fill { height: 100%; background: var(--mj-text-primary, #1a1a1a); border-radius: 3px; }

        .hm__quick { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .hm__quick-tile { display: block; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 18px; text-decoration: none; color: inherit; transition: border-color 0.15s; cursor: pointer; text-align: left; font-family: inherit; font-size: inherit; width: 100%; }
        .hm__quick-tile:hover { border-color: var(--mj-brand-primary, #6366f1); }
        .hm__quick-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
        .hm__quick-title { font-size: 14px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin: 0 0 4px; line-height: 1.3; }
        .hm__quick-sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin: 0; }

        .hm__foot-link { color: var(--mj-text-secondary, #71717a); font-size: 12px; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; border: none; background: transparent; font-family: inherit; padding: 0; }
        .hm__foot-link:hover { color: var(--mj-brand-primary, #6366f1); }

        @media (max-width: 1100px) {
            .hm__okr-grid { grid-template-columns: 1fr; }
            .hm__split { grid-template-columns: 1fr; }
            .hm__quick { grid-template-columns: repeat(2, 1fr); }
            .hm__title { font-size: 26px; }
        }
    `]
})
export class HomeSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.loadExtras();
        } else if (!v) {
            this._companyId = '';
            this.objectives = [];
            this.recentLearnings = [];
            this.objectivesLoaded = false;
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() companyName = '';
    @Input() blogs: marketingBlogEntity[] = [];
    @Input() socialPosts: marketingSocialPostEntity[] = [];
    @Input() newsletters: marketingNewsletterEntity[] = [];
    @Input() recommendations: marketingMarketingRecommendationEntity[] = [];
    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    objectives: marketingObjectiveEntity[] = [];
    objectivesLoaded = false;
    recentLearnings: marketingAgentLearningEntity[] = [];

    private async loadExtras(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        try {
            const rv = new RunView();
            const [objRes, lrnRes] = await rv.RunViews([
                {
                    EntityName: 'Objectives',
                    ExtraFilter: `CompanyID='${cid}' AND IsActive=1`,
                    OrderBy: 'Sequence ASC, __mj_CreatedAt DESC',
                    MaxRows: 3,
                    ResultType: 'simple',
                },
                {
                    EntityName: 'Agent Learnings',
                    ExtraFilter: `(CompanyID='${cid}' OR IsPortfolioLevel=1) AND Confidence='High'`,
                    OrderBy: 'DateRecorded DESC',
                    MaxRows: 2,
                    ResultType: 'simple',
                },
            ]);
            if (objRes.Success) this.objectives = (objRes.Results || []) as marketingObjectiveEntity[];
            if (lrnRes.Success) this.recentLearnings = (lrnRes.Results || []) as marketingAgentLearningEntity[];
            this.objectivesLoaded = true;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[HomeSection] loadExtras failed: ${msg}`);
        } finally {
            this.cdr.detectChanges();
        }
    }

    pad(n: number): string { return n.toString().padStart(2, '0'); }
    formatNum(n: number | null | undefined): string {
        if (n == null) return '—';
        return Number.isInteger(n) ? String(n) : n.toFixed(1);
    }
    progressPct(o: marketingObjectiveEntity): number {
        if (o.TargetValue == null || o.TargetValue === 0) return 0;
        return Math.max(0, Math.min(100, ((o.CurrentValue ?? 0) / o.TargetValue) * 100));
    }
    objectiveLabel(s: string): string {
        switch (s) {
            case 'OnTrack': return 'On track';
            case 'AtRisk': return 'At risk';
            default: return s || '—';
        }
    }
    objectiveVariant(s: string): 'good' | 'warn' | 'bad' | 'muted' | 'info' {
        switch (s) {
            case 'OnTrack':
            case 'Achieved': return 'good';
            case 'AtRisk': return 'warn';
            case 'Blocked':
            case 'Missed': return 'bad';
            default: return 'muted';
        }
    }
    learningVariant(c: string | undefined): 'good' | 'warn' | 'bad' | 'muted' {
        switch (c) {
            case 'High': return 'good';
            case 'Medium': return 'warn';
            case 'Low': return 'bad';
            default: return 'muted';
        }
    }
    openLearning(id: string): void {
        this.navigateWithId('experiments/learnings/detail', id);
    }

    quickLinks = [
        { label: 'Strategy', path: 'strategy/doc', title: 'Living strategy doc', sub: 'Objectives, channel mix, recommendations' },
        { label: 'Programs', path: 'programs', title: 'Evergreen operations', sub: 'Blogs, social, email in flight' },
        { label: 'Experiments', path: 'experiments', title: 'Hypothesis backlog', sub: 'Phase 2 — coming soon' },
        { label: 'Analytics', path: 'analytics', title: 'Performance & pipeline', sub: 'Channel ROI, attribution' },
    ];

    get todayLabel(): string {
        return new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    }

    get greeting(): string {
        const h = new Date().getHours();
        if (h < 12) return 'Good morning';
        if (h < 18) return 'Good afternoon';
        return 'Good evening';
    }

    get shippingItems(): { channel: string; title: string; status: string; statusVariant: 'good' | 'warn' | 'bad' | 'info' | 'muted' }[] {
        const items: { channel: string; title: string; status: string; statusVariant: 'good' | 'warn' | 'bad' | 'info' | 'muted' }[] = [];
        for (const b of this.blogs.slice(0, 5)) {
            const status = String(b.Get('Status') ?? '');
            items.push({ channel: 'Blog', title: b.Title || 'Untitled blog', status, statusVariant: this.toVariant(status) });
        }
        for (const sp of this.socialPosts.slice(0, 3)) {
            const status = String(sp.Get('Status') ?? sp.Get('ContentStatusID') ?? '');
            const content = String(sp.Get('Content') ?? '').slice(0, 60);
            items.push({ channel: 'Social', title: content || 'Social post', status, statusVariant: this.toVariant(status) });
        }
        for (const n of this.newsletters.slice(0, 2)) {
            const status = String(n.Get('Status') ?? '');
            const title = String(n.Get('Subject') ?? n.Get('Title') ?? '');
            items.push({ channel: 'Email', title: title || 'Newsletter', status, statusVariant: this.toVariant(status) });
        }
        return items;
    }

    categoryVariant(category: string): 'good' | 'warn' | 'bad' | 'info' | 'muted' {
        switch (category) {
            case 'Spend': return 'warn';
            case 'Channel':
            case 'Content':
            case 'Plan': return 'info';
            case 'Experiment': return 'good';
            default: return 'muted';
        }
    }

    private toVariant(status: string): 'good' | 'warn' | 'bad' | 'info' | 'muted' {
        const s = status.toLowerCase();
        if (s.includes('publish') || s.includes('approved') || s.includes('ready') || s.includes('scheduled')) return 'good';
        if (s.includes('review') || s.includes('draft')) return 'warn';
        if (s.includes('reject')) return 'bad';
        return 'muted';
    }
}
